import { Fragment, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { EstadoBadge, FilterGroup, Pagination, showToast } from '@aosenuma/ui';
import {
  numParcelaCampo,
  tituloParcelaCampo,
  tituloParcelaConTitular,
  type EstadoSemaforo,
  type VParcelaTecnica,
} from '@aosenuma/lib-types';
import { invalidateCache, isSupabaseConfigured, getSupabase, useExpedientesList, useIrregularidadesList, useParcelasTecnicas, useSession, mapSchemaTipoDocToCatalogDocId, bffFetch, type IrregularidadActivaRow } from '@aosenuma/lib-supabase';
import {
  DOCS_EJIDAL,
  DOCS_PRIVADA,
  DOCS_EJIDAL_GROUPS,
  DOCS_PRIVADA_GROUPS,
} from '../expedienteDocs/catalogoDocumentos';

type TipoTenencia = 'ejido' | 'privado';

interface DocSubido {
  id: string;
  nombre: string;
  fecha: string;
  kb: number;
  /** Llave R2 del archivo. Si está presente, el modal pide signed URL al
   *  BFF (`POST /v1/download/url`) y muestra el archivo real (img/pdf
   *  embebido + descarga funcional). Si está ausente (mock data en DEV),
   *  fallback a placeholder. */
  r2_key?: string;
}

interface ExpEntry {
  parcela_id: string;
  num_parcela: number | null;
  nom_nucleo: string;
  municipio: string;
  area_ha: number;
  estado: EstadoSemaforo;
  tipo: TipoTenencia;
  gestor: string;
  fecha_captura: string;
  docs_subidos: DocSubido[];
  tiene_irregularidad: boolean;
  irregularidad_desc?: string;
  /** Filas activas de `irregularidades` (resuelta=false) para esta parcela.
   *  Usadas para construir el tooltip detallado del banner ⚠. Mig 0074
   *  garantiza una sola por (parcela, subtipo). En mocks queda vacío. */
  irregularidades_detalle?: IrregularidadActivaRow[];
  /** Desde v_parcelas_tecnicas / captura; ausente en mocks antiguos. */
  titular_masked?: string | null;
}

/** Texto multilínea para el `title=""` del banner ⚠. Diseñado para tooltip
 *  nativo: una entrada por irregularidad con subtipo, gravedad y la
 *  descripción si existe, separadas por línea en blanco. */
function buildIrregularidadesTooltip(rows: IrregularidadActivaRow[]): string {
  if (rows.length === 0) return '';
  return rows
    .map((r, i) => {
      const head = rows.length > 1
        ? `${i + 1}. ${r.subtipo} · ${r.gravedad}`
        : `${r.subtipo} · ${r.gravedad}`;
      const desc = r.descripcion?.trim();
      return desc ? `${head}\n— ${desc}` : head;
    })
    .join('\n\n');
}

const MOCK_EXP_DRAFT: Omit<ExpEntry, 'num_parcela'>[] = [
  {
    parcela_id: 'HUM-P0042', nom_nucleo: 'LOS MARTINEZ Y ANEXOS', municipio: 'Huimilpan',
    titular_masked: 'MARTINEZ G. L.',
    area_ha: 4.21, estado: 'en_negociacion', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-28', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'certificado_parcelario',  nombre: 'certificado_HUM0042.pdf', fecha: '2026-04-28', kb: 820 },
      { id: 'identificacion_oficial',  nombre: 'INE_Martinez.pdf',        fecha: '2026-04-28', kb: 1200 },
      { id: 'curp',                    nombre: 'CURP_Martinez.pdf',        fecha: '2026-04-29', kb: 180 },
      { id: 'datos_bancarios_clabe',   nombre: 'CLABE_bancaria.jpg',       fecha: '2026-04-29', kb: 310 },
      { id: 'acta_nacimiento',         nombre: 'acta_nacimiento.pdf',      fecha: '2026-04-30', kb: 540 },
      { id: 'rfc_homoclave',           nombre: 'RFC_Martinez.pdf',         fecha: '2026-04-30', kb: 200 },
    ],
  },
  {
    parcela_id: 'HUM-P0058', nom_nucleo: 'LOS MARTINEZ Y ANEXOS', municipio: 'Huimilpan',
    area_ha: 2.84, estado: 'en_valoracion', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-27', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'certificado_parcelario', nombre: 'cert_parcelario_HUM0058.pdf', fecha: '2026-04-27', kb: 750 },
      { id: 'identificacion_oficial', nombre: 'INE_Ramirez.pdf',             fecha: '2026-04-27', kb: 1100 },
      { id: 'acta_asamblea_delegacion', nombre: 'acta_asamblea.pdf',         fecha: '2026-04-28', kb: 3100 },
    ],
  },
  {
    parcela_id: 'HUM-P0091', nom_nucleo: 'LOS MARTINEZ Y ANEXOS', municipio: 'Huimilpan',
    area_ha: 6.12, estado: 'en_negociacion', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-25', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'certificado_parcelario',       nombre: 'cert_HUM0091.pdf',        fecha: '2026-04-25', kb: 900 },
      { id: 'identificacion_oficial',       nombre: 'INE_Hernandez.pdf',       fecha: '2026-04-25', kb: 1400 },
      { id: 'curp',                         nombre: 'CURP_Hernandez.pdf',      fecha: '2026-04-26', kb: 190 },
      { id: 'comprobante_domicilio',        nombre: 'comprobante_domicilio.pdf', fecha: '2026-04-26', kb: 650 },
      { id: 'rfc_homoclave',               nombre: 'RFC_Hernandez.pdf',       fecha: '2026-04-26', kb: 210 },
      { id: 'acta_nacimiento',             nombre: 'acta_nac_Hernandez.pdf',  fecha: '2026-04-27', kb: 530 },
      { id: 'constancia_derechos_ejidales', nombre: 'constancia_ejidal.pdf',  fecha: '2026-04-27', kb: 420 },
      { id: 'datos_bancarios_clabe',        nombre: 'CLABE_Hernandez.jpg',    fecha: '2026-04-28', kb: 280 },
    ],
  },
  {
    parcela_id: 'HUM-P0103', nom_nucleo: 'LOS MARTINEZ Y ANEXOS', municipio: 'Huimilpan',
    area_ha: 1.05, estado: 'inviable', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-20', tiene_irregularidad: true, irregularidad_desc: 'Gravamen activo · Alta',
    docs_subidos: [
      { id: 'identificacion_oficial', nombre: 'INE_Lopez.pdf', fecha: '2026-04-20', kb: 980 },
    ],
  },
  {
    parcela_id: 'COR-P0017', nom_nucleo: 'SAN RAFAEL', municipio: 'Corregidora',
    area_ha: 9.58, estado: 'en_valoracion', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-22', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'certificado_parcelario', nombre: 'cert_COR0017.pdf',  fecha: '2026-04-22', kb: 870 },
      { id: 'identificacion_oficial', nombre: 'INE_Gomez.pdf',     fecha: '2026-04-22', kb: 1300 },
      { id: 'curp',                   nombre: 'CURP_Gomez.pdf',    fecha: '2026-04-23', kb: 175 },
      { id: 'comprobante_domicilio',  nombre: 'comprobante.pdf',   fecha: '2026-04-23', kb: 700 },
    ],
  },
  {
    parcela_id: 'COR-P0031', nom_nucleo: 'SAN RAFAEL', municipio: 'Corregidora',
    area_ha: 3.40, estado: 'sin_prospectar', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-29', tiene_irregularidad: false,
    docs_subidos: [],
  },
  {
    parcela_id: 'COR-P0045', nom_nucleo: 'SAN RAFAEL', municipio: 'Corregidora',
    area_ha: 5.77, estado: 'en_negociacion', tipo: 'privado', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-18', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'escritura_publica',     nombre: 'escritura_COR0045.pdf',   fecha: '2026-04-18', kb: 2400 },
      { id: 'no_adeudo_predial',     nombre: 'boleta_predial.pdf',       fecha: '2026-04-18', kb: 430 },
      { id: 'identificacion_oficial', nombre: 'INE_Castillo.pdf',       fecha: '2026-04-19', kb: 1100 },
      { id: 'curp',                  nombre: 'CURP_Castillo.pdf',        fecha: '2026-04-19', kb: 190 },
      { id: 'datos_bancarios_clabe', nombre: 'CLABE_Castillo.jpg',       fecha: '2026-04-20', kb: 295 },
    ],
  },
  {
    parcela_id: 'HUM-P0114', nom_nucleo: 'EL CAPULÍN', municipio: 'Huimilpan',
    area_ha: 2.10, estado: 'sin_prospectar', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-30', tiene_irregularidad: false,
    docs_subidos: [],
  },
  {
    parcela_id: 'HUM-P0122', nom_nucleo: 'EL CAPULÍN', municipio: 'Huimilpan',
    area_ha: 7.33, estado: 'en_valoracion', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-26', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'certificado_parcelario', nombre: 'cert_HUM0122.pdf', fecha: '2026-04-26', kb: 810 },
      { id: 'identificacion_oficial', nombre: 'INE_Torres.pdf',   fecha: '2026-04-26', kb: 1050 },
      { id: 'curp',                   nombre: 'CURP_Torres.pdf',  fecha: '2026-04-27', kb: 185 },
    ],
  },
  {
    parcela_id: 'HUM-P0139', nom_nucleo: 'EL CAPULÍN', municipio: 'Huimilpan',
    area_ha: 1.88, estado: 'inviable', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-15', tiene_irregularidad: true, irregularidad_desc: 'Sin título formal · posesión sin formalizar',
    docs_subidos: [
      { id: 'identificacion_oficial', nombre: 'INE_Vargas.pdf', fecha: '2026-04-15', kb: 990 },
    ],
  },
  {
    parcela_id: 'COR-P0062', nom_nucleo: 'LA CAÑADA', municipio: 'Corregidora',
    area_ha: 4.95, estado: 'sin_prospectar', tipo: 'privado', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-05-01', tiene_irregularidad: false,
    docs_subidos: [],
  },
  {
    parcela_id: 'COR-P0078', nom_nucleo: 'LA CAÑADA', municipio: 'Corregidora',
    area_ha: 8.20, estado: 'en_negociacion', tipo: 'privado', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-10', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'escritura_publica',      nombre: 'escritura_COR0078.pdf',   fecha: '2026-04-10', kb: 2800 },
      { id: 'no_adeudo_predial',      nombre: 'predial_COR0078.pdf',     fecha: '2026-04-10', kb: 390 },
      { id: 'identificacion_oficial', nombre: 'INE_Medina.pdf',          fecha: '2026-04-11', kb: 1200 },
      { id: 'curp',                   nombre: 'CURP_Medina.pdf',         fecha: '2026-04-11', kb: 180 },
      { id: 'comprobante_domicilio',  nombre: 'domicilio_Medina.pdf',    fecha: '2026-04-12', kb: 620 },
      { id: 'rfc_homoclave',          nombre: 'RFC_Medina.pdf',          fecha: '2026-04-12', kb: 215 },
      { id: 'datos_bancarios_clabe',  nombre: 'CLABE_Medina.jpg',        fecha: '2026-04-13', kb: 305 },
    ],
  },
  {
    parcela_id: 'HUM-P0155', nom_nucleo: 'LOS MARTINEZ Y ANEXOS', municipio: 'Huimilpan',
    area_ha: 3.62, estado: 'sin_prospectar', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-05-02', tiene_irregularidad: false,
    docs_subidos: [],
  },
  {
    parcela_id: 'HUM-P0161', nom_nucleo: 'LOS MARTINEZ Y ANEXOS', municipio: 'Huimilpan',
    area_ha: 5.04, estado: 'en_valoracion', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-24', tiene_irregularidad: false,
    docs_subidos: [
      { id: 'certificado_parcelario', nombre: 'cert_HUM0161.pdf', fecha: '2026-04-24', kb: 800 },
      { id: 'identificacion_oficial', nombre: 'INE_Reyes.pdf',    fecha: '2026-04-24', kb: 1150 },
    ],
  },
  {
    parcela_id: 'COR-P0089', nom_nucleo: 'SAN RAFAEL', municipio: 'Corregidora',
    area_ha: 2.31, estado: 'inviable', tipo: 'ejido', gestor: 'tecnico_aosenuma',
    fecha_captura: '2026-04-12', tiene_irregularidad: true, irregularidad_desc: 'Adeudo predial vigente · Leve',
    docs_subidos: [
      { id: 'certificado_parcelario', nombre: 'cert_COR0089.pdf', fecha: '2026-04-12', kb: 760 },
      { id: 'identificacion_oficial', nombre: 'INE_Cruz.pdf',     fecha: '2026-04-12', kb: 1080 },
    ],
  },
];

const MOCK_EXP_SAMPLES: ExpEntry[] = MOCK_EXP_DRAFT.map((row) => {
  const n = numParcelaCampo(row.parcela_id, null);
  return { ...row, num_parcela: n === '—' ? null : Number(n) };
});

function muniLabel(m: string): string {
  return m === 'HUIMILPAN' ? 'Huimilpan' : 'Corregidora';
}

/** Construye filas desde `v_parcelas_tecnicas` + tabla `expedientes` +
 *  `irregularidades` activas (vía useIrregularidadesList). */
function buildLiveExpEntries(
  parcelas: VParcelaTecnica[],
  docsByParcela: Map<string, DocSubido[]>,
  irrByParcela: Map<string, IrregularidadActivaRow[]>,
): ExpEntry[] {
  return parcelas.map((p) => {
    const tipo: TipoTenencia =
      p.tipo_propi === 'PROP_PRIV' || p.tipo_propi === 'PROP_FED' ? 'privado' : 'ejido';
    const list = docsByParcela.get(p.parcela_id) ?? [];
    const irrDetalle = irrByParcela.get(p.parcela_id) ?? [];
    const nic = Number(p.irregularidades_count ?? 0);
    return {
      parcela_id: p.parcela_id,
      num_parcela: p.num_parcela ?? null,
      nom_nucleo: p.nom_nucleo ?? '—',
      municipio: muniLabel(p.municipio),
      area_ha: Number(p.area_ha),
      estado: p.estado as EstadoSemaforo,
      tipo,
      gestor: '—',
      fecha_captura: list[0]?.fecha ?? '—',
      docs_subidos: list,
      tiene_irregularidad: Boolean(p.irregular) || nic > 0,
      irregularidad_desc:
        nic > 0 ? `${nic} irregularidad(es)` : p.irregular ? 'Irregular' : undefined,
      irregularidades_detalle: irrDetalle,
      titular_masked: p.titular_masked ?? null,
    };
  });
}

const TENENCIA: { value: string; label: string }[] = [
  { value: '',        label: 'Ambos' },
  { value: 'ejido',   label: 'Ejidal' },
  { value: 'privado', label: 'Privado' },
];

const POR_PAGINA = 10;

// docSlots() removido junto con railDocResumen (acta 4 may): solo se usaba
// para el panel "Documentación en vista" del rail. La completitud por parcela
// se muestra en la fila individual via `expedientes_count` del view.

function fmtFecha(iso: string) {
  const s = iso.trim();
  if (!s || s === '—') return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? s
    : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtKb(kb: number) {
  return kb >= 1000 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
}

/**
 * Label legible para items que caen en "Carga general" (no pertenecen al
 * checklist canónico). El `id` aquí ya viene normalizado vía
 * `mapSchemaTipoDocToCatalogDocId`, así que cubrimos los tipo_doc que NO
 * tienen entrada en DOCS_EJIDAL/DOCS_PRIVADA pero sí pueden llegar a
 * `expedientes` desde otros flujos (contrato firmado, fotos, etc.).
 */
function labelForExpedienteOtro(id: string): string {
  const labels: Record<string, string> = {
    otro: 'Documento adicional',
    contrato_firmado: 'Contrato firmado',
    foto_parcela: 'Foto de la parcela',
    foto_vista_aerea: 'Foto vista aérea',
    foto_visita: 'Foto de visita',
    contrato_arrendamiento: 'Contrato de arrendamiento',
  };
  return labels[id] ?? 'Documento adicional';
}

export function ExpedientesPage() {
  const { parcela_id: parcelaParam } = useParams<{ parcela_id?: string }>();
  const navigate = useNavigate();
  const [busqueda, setBusqueda]         = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo]     = useState('');
  const [pagina, setPagina]             = useState(1);
  const [seleccionada, setSeleccionada] = useState<string | undefined>();
  /**
   * Modal de preview. Guardamos el índice dentro de la lista navegable de
   * subidos (`navegableDocs`) en lugar del doc directo — así el modal puede
   * avanzar/retroceder con teclado o botones manteniendo el orden visual de
   * la checklist + carga general. `null` = modal cerrado.
   */
  const [docPreviewIdx, setDocPreviewIdx] = useState<number | null>(null);
  /** Modal de resolución abierto sobre las irregularidades de la parcela
   *  detalle actual. `null` = cerrado. Solo admin_aosenuma puede abrirlo
   *  (FE oculta el botón); la RPC `resolver_irregularidad` re-valida server-side. */
  const [resolveOpen, setResolveOpen] = useState(false);

  const { rolApp } = useSession();
  const canResolveIrregularidades = rolApp === 'admin_aosenuma';

  const { data: apiRows } = useParcelasTecnicas();
  const { data: expRows } = useExpedientesList();
  const { data: irrRows } = useIrregularidadesList();

  const docsByParcela = useMemo(() => {
    const m = new Map<string, DocSubido[]>();
    for (const e of expRows) {
      const slash = e.r2_key.lastIndexOf('/');
      const nombre = slash >= 0 ? e.r2_key.slice(slash + 1) : e.r2_key;
      const doc: DocSubido = {
        id: mapSchemaTipoDocToCatalogDocId(e.tipo_doc),
        nombre,
        fecha: e.subido_at.slice(0, 10),
        kb: Math.max(1, Math.round(Number(e.size_bytes) / 1024)),
        r2_key: e.r2_key,
      };
      const arr = m.get(e.parcela_id) ?? [];
      arr.push(doc);
      m.set(e.parcela_id, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => b.fecha.localeCompare(a.fecha));
    }
    return m;
  }, [expRows]);

  /** Map parcela_id (código público) → irregularidades activas para esa
   *  parcela. Alimenta el tooltip detallado del banner ⚠. */
  const irrByParcela = useMemo(() => {
    const m = new Map<string, IrregularidadActivaRow[]>();
    for (const ir of irrRows) {
      const arr = m.get(ir.parcela_id) ?? [];
      arr.push(ir);
      m.set(ir.parcela_id, arr);
    }
    for (const arr of m.values()) {
      // Más severas primero, luego más recientes.
      arr.sort((a, b) => {
        const sev = (g: string) =>
          g === 'bloqueante' ? 4 : g === 'alta' ? 3 : g === 'media' ? 2 : 1;
        return sev(b.gravedad) - sev(a.gravedad)
            || b.created_at.localeCompare(a.created_at);
      });
    }
    return m;
  }, [irrRows]);

  const todasParcelas = useMemo(() => {
    // En prod: NO fallback a mock. Si la query falla o no hay parcelas,
    // mostramos empty state real (los mocks idénticos cada vez se sentían
    // como "no se actualiza"). En DEV mantenemos los samples para preview.
    const SHOW_MOCKS = import.meta.env.DEV || !import.meta.env.PROD;
    if (!isSupabaseConfigured()) return SHOW_MOCKS ? MOCK_EXP_SAMPLES : [];
    if (apiRows.length === 0) return SHOW_MOCKS ? MOCK_EXP_SAMPLES : [];
    return buildLiveExpEntries(apiRows, docsByParcela, irrByParcela);
  }, [apiRows, docsByParcela, irrByParcela]);

  const filtradas = useMemo(() => {
    const qRaw = busqueda.trim();
    // Normalización: NFD para que "javier" matchee "JÁVIER" sin acentos.
    const qNorm = qRaw.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
    const qDigits = qRaw.replace(/\D/g, '');
    function norm(s: string | null | undefined): string {
      if (!s) return '';
      return s.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
    }
    return todasParcelas.filter((p) => {
      if (qRaw) {
        const numCampo = numParcelaCampo(p.parcela_id, p.num_parcela);
        // Buscamos en TODOS los campos visibles del expediente: ID público,
        // ejido (nom_nucleo), municipio, titular enmascarado. CURP/INE son
        // PII cifrada y NO se buscan aquí (requeriría descifra batch con MFA).
        const matchText =
          norm(p.parcela_id).includes(qNorm) ||
          norm(p.nom_nucleo).includes(qNorm) ||
          norm(p.municipio).includes(qNorm) ||
          norm(p.titular_masked).includes(qNorm);
        const matchNum =
          (qDigits.length > 0 && numCampo.includes(qDigits)) ||
          (qRaw.length > 0 && numCampo === qRaw);
        if (!matchText && !matchNum) return false;
      }
      if (filtroEstado && p.estado !== filtroEstado) return false;
      if (filtroTipo && p.tipo !== filtroTipo) return false;
      return true;
    });
  }, [busqueda, filtroEstado, filtroTipo, todasParcelas]);

  useEffect(() => {
    if (!parcelaParam) {
      setSeleccionada(undefined);
      return;
    }
    const exists = todasParcelas.some((p) => p.parcela_id === parcelaParam);
    setSeleccionada(exists ? parcelaParam : undefined);
  }, [parcelaParam, todasParcelas]);

  useEffect(() => {
    setPagina(1);
  }, [todasParcelas.length]);

  useEffect(() => {
    if (!parcelaParam) return;
    const fIdx = filtradas.findIndex((p) => p.parcela_id === parcelaParam);
    if (fIdx >= 0) {
      setPagina(Math.floor(fIdx / POR_PAGINA) + 1);
    }
  }, [parcelaParam, filtradas]);

  const totalPags = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const paginas   = filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const detalle = todasParcelas.find((p) => p.parcela_id === seleccionada);
  const detalleDocs = detalle ? (detalle.tipo === 'ejido' ? DOCS_EJIDAL : DOCS_PRIVADA) : [];
  const detalleDocGroups = detalle ? (detalle.tipo === 'ejido' ? DOCS_EJIDAL_GROUPS : DOCS_PRIVADA_GROUPS) : [];

  /**
   * "Carga general": todo doc que NO pertenece al checklist canónico
   * (catálogo UI). Cubre tres orígenes:
   *   - dropzone general en Captura → tipo_doc = 'otro'
   *   - contrato firmado del flujo de cliente → tipo_doc = 'contrato_firmado'
   *   - cualquier tipo_doc futuro (fotos, contratos auxiliares, etc.) que
   *     llegue al expediente sin tener fila en el checklist.
   * Sin esta inclusión amplia, el doc quedaba invisible en la UI: ni en
   * checklist (no matchea) ni en "Carga general" (cuando solo aceptaba
   * literal === 'otro'). El criterio es: si no aparece en `detalleDocs`,
   * va aquí para que el técnico lo vea.
   */
  const expedienteOtro = useMemo(() => {
    if (!detalle) return [] as DocSubido[];
    const checklistIds = new Set(detalleDocs.map((d) => d.id));
    return detalle.docs_subidos.filter((d) => !checklistIds.has(d.id));
  }, [detalle, detalleDocs]);

  /**
   * Cuenta cuántos ítems del checklist tienen un upload coincidente. Usa
   * `Set` para evitar O(N×M) cuando un parcel tiene muchos docs subidos. */
  const checklistDoneCount = useMemo(() => {
    if (!detalle) return 0;
    const subidoIds = new Set(detalle.docs_subidos.map((s) => s.id));
    return detalleDocs.filter((d) => subidoIds.has(d.id)).length;
  }, [detalle, detalleDocs]);

  /**
   * Orden navegable de docs subidos: primero los del checklist (en orden de
   * catálogo, saltando pendientes) y al final los de "Carga general". El
   * modal de preview avanza/retrocede sobre este orden con ← / → o los
   * botones de la cabecera. Manteniendo la misma instancia de `DocSubido`
   * que se renderiza en la lista, el lookup por identidad (`navegableDocs
   * .findIndex((n) => n === subido)`) es exacto y barato. */
  const navegableDocs = useMemo<DocSubido[]>(() => {
    if (!detalle) return [];
    const out: DocSubido[] = [];
    for (const d of detalleDocs) {
      const s = detalle.docs_subidos.find((x) => x.id === d.id);
      if (s) out.push(s);
    }
    for (const s of expedienteOtro) out.push(s);
    return out;
  }, [detalle, detalleDocs, expedienteOtro]);

  // Si la lista navegable cambia (cambio de parcela, doc agregado/borrado),
  // cerramos el modal en lugar de quedar apuntando a un índice viejo.
  useEffect(() => {
    if (docPreviewIdx == null) return;
    if (docPreviewIdx < 0 || docPreviewIdx >= navegableDocs.length) {
      setDocPreviewIdx(null);
    }
  }, [navegableDocs, docPreviewIdx]);

  /**
   * Navegación entre expedientes (parcelas) usando el orden de `filtradas`,
   * que es lo que el técnico ve en la columna derecha. Si filtra/busca,
   * prev/next respeta ese subconjunto. Si la parcela seleccionada no está
   * en `filtradas` (caso raro: viene por URL y los filtros la excluyen),
   * `idxFiltradas = -1` y los botones quedan deshabilitados.
   */
  const idxFiltradas = useMemo(() => {
    if (!detalle) return -1;
    return filtradas.findIndex((p) => p.parcela_id === detalle.parcela_id);
  }, [filtradas, detalle]);
  const prevParcelaId = idxFiltradas > 0 ? filtradas[idxFiltradas - 1]?.parcela_id ?? null : null;
  const nextParcelaId =
    idxFiltradas >= 0 && idxFiltradas < filtradas.length - 1
      ? filtradas[idxFiltradas + 1]?.parcela_id ?? null
      : null;

  function irAParcela(pid: string) {
    // Cerramos el modal de preview por si quedó abierto al cambiar de
    // parcela (la lista navegable de docs es otra). El listener de
    // ←/→ del modal toma precedencia mientras esté montado, así que
    // este branch típicamente no se llega con modal abierto.
    setDocPreviewIdx(null);
    navigate(`/expedientes/${pid}`);
  }

  // Atajos ←/→ a nivel página cuando hay detalle. Ignoramos:
  //   - modal de preview abierto (él tiene su propio listener para los
  //     mismos teclas — navegar entre archivos)
  //   - foco en input/textarea/select (búsqueda, filtros)
  //   - modificadores (Ctrl/Cmd/Alt) para no pisar atajos del navegador
  useEffect(() => {
    if (!detalle) return;
    function onKey(e: KeyboardEvent) {
      if (docPreviewIdx != null) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft' && prevParcelaId) {
        e.preventDefault();
        irAParcela(prevParcelaId);
      } else if (e.key === 'ArrowRight' && nextParcelaId) {
        e.preventDefault();
        irAParcela(nextParcelaId);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle, prevParcelaId, nextParcelaId, docPreviewIdx]);

  // Hero del empty-state: KPIs rápidos + cards de últimas actualizadas.
  // Solo se calculan cuando NO hay parcela seleccionada (viewport del centro
  // está en modo hero); cuando hay detalle el centro pinta otra cosa.
  const hero = useMemo(() => {
    if (detalle) return null;
    const total = todasParcelas.length;
    const contratadas = todasParcelas.filter((p) => p.estado === 'contratada').length;
    const enNegociacion = todasParcelas.filter((p) => p.estado === 'en_negociacion').length;
    const enValoracion = todasParcelas.filter((p) => p.estado === 'en_valoracion').length;
    const sinProspectar = todasParcelas.filter((p) => p.estado === 'sin_prospectar').length;
    const inviables = todasParcelas.filter((p) => p.estado === 'inviable').length;
    const irregulares = todasParcelas.filter((p) => p.tiene_irregularidad).length;
    // Últimas 6 con actividad documental real (fecha_captura ≠ "—"). Si el
    // catálogo está completamente sin documentar, fallback a las primeras
    // 6 por orden del catálogo (siempre enseñamos algo útil).
    const conActividad = todasParcelas.filter((p) => p.fecha_captura && p.fecha_captura !== '—');
    const recientes = (conActividad.length > 0 ? conActividad : todasParcelas)
      .slice()
      .sort((a, b) => (b.fecha_captura ?? '').localeCompare(a.fecha_captura ?? ''))
      .slice(0, 6);
    return { total, contratadas, enNegociacion, enValoracion, sinProspectar, inviables, irregulares, recientes };
  }, [detalle, todasParcelas]);

  // railDocResumen removido (acta 4 may): el panel "Documentación en vista" +
  // el contador agregado de docs por filas no aportaban en el rail de búsqueda.
  // El progreso por parcela se muestra en su fila individual y al seleccionarla.

  function resetFiltros() {
    setBusqueda(''); setFiltroEstado(''); setFiltroTipo(''); setPagina(1);
  }

  function seleccionar(id: string) {
    setSeleccionada((prev) => (prev === id ? undefined : id));
  }

  // Modo del split:
  //   "hero"   → no hay parcela seleccionada. El centro toma todo el ancho
  //              con el hero (search + chips + lista paginada). El rail
  //              no se renderiza, no hay duplicación.
  //   "detail" → hay parcela seleccionada. El centro muestra el expediente,
  //              el rail aparece colapsado a una franja con el botón
  //              "Buscar otra parcela" que deselecciona y vuelve a hero.
  // CSS aplica grid-template-columns acorde via data-mode.
  const splitMode: 'hero' | 'detail' = detalle ? 'detail' : 'hero';

  return (
    <div className="exp-split" data-mode={splitMode}>

      {/* ── Centro: hero o detalle del expediente ────── */}
      <aside className="exp-center-panel glass-panel">
        <div className="exp-track-body">
          {!detalle ? (
            <div className="exp-hero">
              {/* Encabezado + buscador: estado compartido con filtros y lista. */}
              <header className="exp-hero-head">
                <div className="exp-hero-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" width={28} height={28} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                </div>
                <h2 className="exp-hero-title">Buscar expediente</h2>
                <p className="exp-hero-sub">
                  Encuentra una parcela por número, ID, ejido o municipio.
                  {hero && (
                    <span className="exp-hero-sub-count"> · {hero.total} expediente{hero.total === 1 ? '' : 's'}</span>
                  )}
                </p>
                <div className="exp-hero-search">
                  <span className="exp-hero-search-icon" aria-hidden>
                    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <input
                    type="search"
                    className="exp-hero-search-input"
                    placeholder="Ej: 70 · HUM-P0042 · Corregidora"
                    value={busqueda}
                    onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                    aria-label="Buscar expediente por número, ID, ejido o municipio"
                  />
                  {busqueda && (
                    <button
                      type="button"
                      className="exp-hero-search-clear"
                      onClick={() => { setBusqueda(''); setPagina(1); }}
                      aria-label="Limpiar búsqueda"
                    >×</button>
                  )}
                </div>
              </header>

              {/* Filtros discretos: estado (con conteo inline) + tenencia.
                  Reusan `FilterGroup` (chip-btn--soft) — mismo componente y
                  variant que se usa en TabInventario y FilterBar para que
                  el system design quede consistente. */}
              {hero && (
                <div className="exp-hero-filters">
                  <FilterGroup
                    label="Estado"
                    options={[
                      { value: '',                label: <>Todos <span className="exp-hero-chip-count">{hero.total.toLocaleString('es-MX')}</span></> },
                      { value: 'contratada',      label: <>Contratadas <span className="exp-hero-chip-count">{hero.contratadas.toLocaleString('es-MX')}</span></> },
                      { value: 'en_negociacion',  label: <>En negociación <span className="exp-hero-chip-count">{hero.enNegociacion.toLocaleString('es-MX')}</span></> },
                      { value: 'en_valoracion',   label: <>En valoración <span className="exp-hero-chip-count">{hero.enValoracion.toLocaleString('es-MX')}</span></> },
                      { value: 'sin_prospectar',  label: <>Sin prospectar <span className="exp-hero-chip-count">{hero.sinProspectar.toLocaleString('es-MX')}</span></> },
                      { value: 'inviable',        label: <>Inviables <span className="exp-hero-chip-count">{hero.inviables.toLocaleString('es-MX')}</span></> },
                    ]}
                    active={filtroEstado}
                    onChange={(v) => { setFiltroEstado(v); setPagina(1); }}
                    variant="soft"
                  />
                  <FilterGroup
                    label="Tenencia"
                    options={TENENCIA}
                    active={filtroTipo}
                    onChange={(v) => { setFiltroTipo(v); setPagina(1); }}
                    variant="soft"
                  />
                </div>
              )}

              {/* Lista paginada (todas las filtradas). Sustituye al rail
                  derecho cuando no hay parcela seleccionada — UNA sola
                  superficie de búsqueda, sin duplicación. Cuando el user
                  selecciona una parcela el centro pasa a detalle y el rail
                  aparece colapsado con "Buscar otra parcela" → deselecciona
                  y vuelve a este hero. */}
              <section className="exp-hero-recientes" aria-labelledby="exp-hero-results-title">
                <div className="exp-hero-results-head">
                  <h3 id="exp-hero-results-title" className="exp-hero-section-title">
                    {busqueda || filtroEstado || filtroTipo ? 'Resultados' : 'Últimas actualizadas'}
                  </h3>
                  <span className="exp-hero-results-count">
                    {filtradas.length.toLocaleString('es-MX')} parcela{filtradas.length === 1 ? '' : 's'}
                  </span>
                </div>
                {paginas.length === 0 ? (
                  <div className="exp-hero-empty-results">
                    Ningún expediente coincide con la búsqueda.
                    {(busqueda || filtroEstado || filtroTipo) && (
                      <button type="button" className="exp-hero-empty-clear" onClick={resetFiltros}>
                        Limpiar filtros
                      </button>
                    )}
                  </div>
                ) : (
                  <ul className="exp-hero-list">
                    {paginas.map((p) => {
                      const docsLen = p.docs_subidos.length;
                      const totalDocs = (p.tipo === 'ejido' ? DOCS_EJIDAL : DOCS_PRIVADA).length;
                      const pct = totalDocs > 0 ? Math.round((docsLen / totalDocs) * 100) : 0;
                      return (
                        <li
                          key={p.parcela_id}
                          className="exp-hero-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => seleccionar(p.parcela_id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              seleccionar(p.parcela_id);
                            }
                          }}
                          aria-label={`Abrir expediente de ${tituloParcelaConTitular(p.parcela_id, p.num_parcela, p.titular_masked)}`}
                        >
                          <div className="exp-hero-row-title-stack">
                            <span className="exp-hero-row-num">{tituloParcelaConTitular(p.parcela_id, p.num_parcela, p.titular_masked)}</span>
                            <span className="exp-hero-row-id-mono">{p.parcela_id}</span>
                          </div>
                          <span className="exp-hero-row-meta">
                            {p.nom_nucleo} <span className="exp-hero-row-sep">·</span> {p.municipio} <span className="exp-hero-row-sep">·</span> {p.area_ha.toFixed(2)} ha
                          </span>
                          <div className="exp-hero-row-side">
                            <span className="exp-hero-row-progress" title={`${docsLen} de ${totalDocs} documentos`}>
                              <span className="exp-hero-row-progress-bar" aria-hidden>
                                <span className="exp-hero-row-progress-fill" style={{ width: `${pct}%` }} />
                              </span>
                              <span className="exp-hero-row-progress-count">{docsLen}/{totalDocs}</span>
                            </span>
                            <span className="exp-hero-row-fecha">{fmtFecha(p.fecha_captura)}</span>
                            <EstadoBadge value={p.estado} />
                            {p.tiene_irregularidad && (
                              <span className="exp-hero-row-alert" title={p.irregularidad_desc} aria-label="Irregularidad">⚠</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {totalPags > 1 && (
                  <Pagination
                    current={pagina}
                    total={totalPags}
                    onChange={setPagina}
                    compact
                    size="sm"
                    className="exp-hero-pag"
                  />
                )}
              </section>
            </div>
          ) : (
            <div className="exp-detail exp-detail--track" key={detalle.parcela_id}>
            {/* Info header */}
            <div className="exp-detail-header">
            {/* Navegación entre parcelas del listado filtrado actual.
                Solo aparece si hay >1 resultado en `filtradas`. Atajos ←/→
                a nivel página (effect arriba). Se oculta cuando el modal
                de preview está abierto para no confundir con la nav de
                archivos de ese modal. */}
            {filtradas.length > 1 && (
              <div className="exp-detail-nav-row" role="group" aria-label="Navegar entre expedientes">
                <button
                  type="button"
                  className="exp-detail-nav-btn"
                  onClick={() => prevParcelaId && irAParcela(prevParcelaId)}
                  disabled={!prevParcelaId}
                  aria-label="Expediente anterior (←)"
                  title="Expediente anterior (←)"
                >
                  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  <span>Anterior</span>
                </button>
                {idxFiltradas >= 0 && (
                  <span className="exp-detail-nav-counter" aria-label={`Expediente ${idxFiltradas + 1} de ${filtradas.length}`}>
                    {idxFiltradas + 1} <span className="exp-detail-nav-counter-sep">/</span> {filtradas.length}
                  </span>
                )}
                <button
                  type="button"
                  className="exp-detail-nav-btn"
                  onClick={() => nextParcelaId && irAParcela(nextParcelaId)}
                  disabled={!nextParcelaId}
                  aria-label="Expediente siguiente (→)"
                  title="Expediente siguiente (→)"
                >
                  <span>Siguiente</span>
                  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            )}
            <div className="exp-detail-id-row">
              <div className="exp-detail-title-stack">
                {/* Título base "Parcela N" + bajo él, el titular como
                    sub-línea prominente (alineado con ParcelaInfoBar
                    `.pb-titular`). Si no hay titular registrado, mostramos
                    un empty state explícito en lugar de omitir la línea
                    — así el técnico sabe que el campo existe y puede
                    capturarlo desde Captura. */}
                <h2 className="exp-detail-id">{tituloParcelaCampo(detalle.parcela_id, detalle.num_parcela)}</h2>
                {detalle.titular_masked ? (
                  <div className="exp-detail-titular" title={detalle.titular_masked}>
                    {detalle.titular_masked}
                  </div>
                ) : (
                  <div className="exp-detail-titular exp-detail-titular--empty">
                    Sin titular registrado
                  </div>
                )}
                <span className="exp-detail-id-system">{detalle.parcela_id}</span>
              </div>
              <EstadoBadge value={detalle.estado} />
            </div>
              <div className="exp-detail-meta-grid">
                {/* Titular como cell con la misma jerarquía que el resto
                    del meta grid: el técnico/cliente ve "—" cuando falta
                    el dato, no un hueco. Captura nuevo → backend escribe
                    propietarios.titular_masked → próxima carga lo
                    refleja aquí (TTL 30s del cache + cacheBus). */}
                <div className="exp-meta-item">
                  <span className="exp-meta-label">Titular</span>
                  <span
                    className={`exp-meta-val${detalle.titular_masked ? '' : ' exp-meta-val--empty'}`}
                  >
                    {detalle.titular_masked ?? '—'}
                  </span>
                </div>
                <div className="exp-meta-item">
                  <span className="exp-meta-label">Ejido / núcleo</span>
                  <span className="exp-meta-val">{detalle.nom_nucleo}</span>
                </div>
                <div className="exp-meta-item">
                  <span className="exp-meta-label">Municipio</span>
                  <span className="exp-meta-val">{detalle.municipio}</span>
                </div>
                <div className="exp-meta-item">
                  <span className="exp-meta-label">Superficie</span>
                  <span className="exp-meta-val">{detalle.area_ha.toFixed(2)} ha</span>
                </div>
                <div className="exp-meta-item">
                  <span className="exp-meta-label">Tenencia</span>
                  <span className="exp-meta-val">{detalle.tipo === 'ejido' ? 'Ejidal' : 'Privada'}</span>
                </div>
                <div className="exp-meta-item">
                  <span className="exp-meta-label">Captura</span>
                  <span className="exp-meta-val">{fmtFecha(detalle.fecha_captura)}</span>
                </div>
              </div>
              {detalle.tiene_irregularidad && detalle.irregularidad_desc && (() => {
                const detallesIrr = detalle.irregularidades_detalle ?? [];
                const tooltip = buildIrregularidadesTooltip(detallesIrr);
                // El botón «Resolver» solo aparece para admin_aosenuma y
                // cuando hay filas reales (no mocks/legacy). El click abre
                // el modal de resolución sobre TODAS las activas de la parcela.
                const showResolveBtn = canResolveIrregularidades && detallesIrr.length > 0;
                return (
                  <div
                    className={`exp-detail-irreg-banner${detallesIrr.length > 0 ? ' has-tooltip' : ''}`}
                    role="status"
                    title={tooltip || undefined}
                    aria-label={
                      tooltip ? `Irregularidades: ${tooltip.replace(/\n+/g, ' · ')}` : undefined
                    }
                  >
                    <span>⚠ {detalle.irregularidad_desc}</span>
                    {showResolveBtn && (
                      <button
                        type="button"
                        className="exp-detail-irreg-resolve-btn"
                        onClick={(e) => {
                          // No queremos que el click escape al `title` del banner
                          // ni dispare scroll/focus extraño.
                          e.stopPropagation();
                          setResolveOpen(true);
                        }}
                        title="Marcar como resuelta (solo admin)"
                      >
                        Resolver
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Progreso documental */}
            <div className="exp-progress-section">
              <div className="exp-progress-label-row">
                <span>Documentación</span>
                <span className="exp-progress-count">
                  <strong style={{ color: 'var(--teal-bright)' }}>{checklistDoneCount}</strong>
                  {' / '}{detalleDocs.length}
                  {expedienteOtro.length > 0 && (
                    <span className="exp-progress-extra"> · +{expedienteOtro.length} general</span>
                  )}
                </span>
              </div>
              <div className="exp-progress-track">
                <div
                  className="exp-progress-fill"
                  style={{
                    width: `${detalleDocs.length ? (checklistDoneCount / detalleDocs.length) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Checklist documental (mismos grupos e ids que Captura paso 3).
                `navegableDocs` se reconstruye en orden visual (checklist +
                carga general) y se pasa al modal de preview para que ← / →
                muevan entre los subidos sin perder el orden de la lista. */}
            <div className="exp-doc-section">
              <ul className="exp-doc-list">
                {detalleDocGroups.map((group, gi) => (
                  <Fragment key={group.label}>
                    <li
                      className={`exp-doc-group-label${gi === 0 ? ' is-first' : ''}`}
                      role="presentation"
                    >
                      {group.label}
                    </li>
                    {group.items.map((doc) => {
                      const idx = detalleDocs.findIndex((d) => d.id === doc.id);
                      const subido = detalle.docs_subidos.find((d) => d.id === doc.id);
                      const navIdx = subido
                        ? navegableDocs.findIndex((n) => n === subido)
                        : -1;
                      const onClickRow = subido
                        ? () => navIdx >= 0 && setDocPreviewIdx(navIdx)
                        : () => {
                            // Pendiente → ir directo a captura paso 3 con el
                            // doc enfocado. Si la parcela aún no existe en
                            // /captura/:id, el router lo creará en CapturaPage
                            // (mismo flujo que «Ver expediente» → «Cargar»).
                            navigate(`/captura/${detalle.parcela_id}?step=3&doc=${doc.id}`);
                          };
                      return (
                        <li
                          key={doc.id}
                          className={`exp-doc-item${subido ? ' is-done is-clickable' : ' is-pending-clickable'}`}
                          style={{ '--idx': idx } as React.CSSProperties}
                          role="button"
                          tabIndex={0}
                          aria-label={
                            subido
                              ? `Abrir preview de ${doc.label}`
                              : `Subir ${doc.label} — abre captura paso 3`
                          }
                          onClick={onClickRow}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClickRow();
                            }
                          }}
                        >
                          <div className="exp-doc-check">
                            {subido ? (
                              <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            ) : null}
                          </div>
                          <div className="exp-doc-info">
                            <span className="exp-doc-label">{doc.label}</span>
                            {!subido && doc.note.trim() !== '' && (
                              <span className="exp-doc-hint">{doc.note}</span>
                            )}
                            {subido && (
                              <span className="exp-doc-file">
                                {subido.nombre}
                                <span className="exp-doc-meta">{fmtFecha(subido.fecha)} · {fmtKb(subido.kb)}</span>
                              </span>
                            )}
                          </div>
                          {!subido && <span className="exp-doc-pending">Pendiente</span>}
                        </li>
                      );
                    })}
                  </Fragment>
                ))}
                {expedienteOtro.length > 0 && (
                  <>
                    <li className="exp-doc-group-label" role="presentation">
                      Carga general
                    </li>
                    {expedienteOtro.map((subido, oi) => {
                      const navIdx = navegableDocs.findIndex((n) => n === subido);
                      return (
                        <li
                          key={`otro-${oi}-${subido.fecha}-${subido.nombre}`}
                          className="exp-doc-item is-done is-clickable"
                          style={{ '--idx': detalleDocs.length + oi } as React.CSSProperties}
                          role="button"
                          tabIndex={0}
                          aria-label={`Abrir preview de ${labelForExpedienteOtro(subido.id)}`}
                          onClick={() => navIdx >= 0 && setDocPreviewIdx(navIdx)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (navIdx >= 0) setDocPreviewIdx(navIdx);
                            }
                          }}
                        >
                          <div className="exp-doc-check">
                            <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                          <div className="exp-doc-info">
                            {/* Etiqueta humana del tipo_doc cuando es uno
                                conocido (contrato firmado, foto, etc.). Para
                                `otro` (dropzone genérico) se muestra "Documento
                                adicional" como antes. */}
                            <span className="exp-doc-label">{labelForExpedienteOtro(subido.id)}</span>
                            <span className="exp-doc-file">
                              {subido.nombre}
                              <span className="exp-doc-meta">{fmtFecha(subido.fecha)} · {fmtKb(subido.kb)}</span>
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </>
                )}
              </ul>
            </div>

          </div>
          )}
        </div>
      </aside>

      {/* Rail derecho: solo aparece cuando hay parcela seleccionada,
          colapsado a una franja con el botón "Buscar otra parcela".
          Click → deselecciona la parcela y vuelve al hero (única
          superficie de búsqueda, sin duplicación con el hero del centro). */}
      {detalle && (
        <aside className="exp-search-rail glass-panel" aria-label="Volver al buscador de expedientes">
          <button
            type="button"
            className="exp-rail-expand-btn"
            onClick={() => setSeleccionada(undefined)}
            title="Buscar otra parcela"
            aria-label="Buscar otra parcela"
          >
            <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span className="exp-rail-expand-label">Buscar otra parcela</span>
          </button>
        </aside>
      )}
      {resolveOpen && detalle && (
        <ResolverIrregularidadesModal
          parcelaCodigo={detalle.parcela_id}
          irregularidades={detalle.irregularidades_detalle ?? []}
          onClose={() => setResolveOpen(false)}
          onResolved={() => {
            // Invalida cache → siguiente render del hook trae sin la resuelta.
            invalidateCache(['irregularidades_list']);
          }}
        />
      )}
      {docPreviewIdx != null && navegableDocs[docPreviewIdx] && (
        <DocPreviewModal
          doc={navegableDocs[docPreviewIdx]}
          currentIndex={docPreviewIdx}
          total={navegableDocs.length}
          onClose={() => setDocPreviewIdx(null)}
          onPrev={
            docPreviewIdx > 0
              ? () => setDocPreviewIdx(docPreviewIdx - 1)
              : undefined
          }
          onNext={
            docPreviewIdx < navegableDocs.length - 1
              ? () => setDocPreviewIdx(docPreviewIdx + 1)
              : undefined
          }
        />
      )}
    </div>
  );
}

/* ── Modal de previsualización ──────────────────────────── */
function DocPreviewModal({
  doc,
  onClose,
  onPrev,
  onNext,
  currentIndex,
  total,
}: {
  doc: DocSubido;
  onClose: () => void;
  /** Si `undefined`, no hay anterior (estamos en el primero). El botón
   *  queda visible pero deshabilitado para no descolocar el layout del
   *  header al pasar de tener navegación a no tenerla. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Para mostrar "3 / 14" en la cabecera. Ambos opcionales — si falta
   *  alguno, no se renderiza el contador. */
  currentIndex?: number;
  total?: number;
}) {
  const ext = doc.nombre.split('.').pop()?.toLowerCase() ?? '';
  const isImg = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'].includes(ext);
  const isPdf = ext === 'pdf';

  // Atajos de teclado del modal: Esc cierra, ←/→ navegan entre docs.
  // Usamos las refs (`onPrev`/`onNext`) que el padre re-genera al cambiar
  // de índice — el effect se re-suscribe cuando cambian, así el callback
  // dentro del listener siempre es el de la posición actual.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      // Evita interferir cuando el foco está en un input/textarea/iframe.
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  // Signed URL fetched from BFF on mount. Si doc.r2_key está ausente
  // (mock data en DEV), no llamamos al BFF y dejamos el placeholder.
  //
  // Política CSP: el iframe/img que muestra el PDF/imagen NO puede
  // apuntar directamente al signedUrl de R2 (CSP child-src/img-src
  // bloquea r2.cloudflarestorage.com en algunos navegadores). En vez de
  // ampliar el CSP, fetch el archivo y crea un blob: URL local — está
  // permitido por defecto. El signedUrl se mantiene aparte para el
  // botón "Descargar" (download attribute soporta cross-origin).
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState<boolean>(!!doc.r2_key);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc.r2_key) return;
    let cancelled = false;
    let blobObjectUrl: string | null = null;
    setLoadingUrl(true);
    setUrlError(null);
    setBlobUrl(null);
    void (async () => {
      try {
        const resp = await bffFetch<{ url: string; expires_at: string }>(
          '/v1/download/url',
          { method: 'POST', body: { r2_key: doc.r2_key } },
        );
        if (cancelled) return;
        setSignedUrl(resp.url);
        // Fetch el archivo como blob para mostrarlo en el iframe/img
        // sin chocar con CSP frame-src/connect-src de R2.
        const fileResp = await fetch(resp.url);
        if (!fileResp.ok) throw new Error(`fetch ${fileResp.status}`);
        const blob = await fileResp.blob();
        if (cancelled) return;
        blobObjectUrl = URL.createObjectURL(blob);
        setBlobUrl(blobObjectUrl);
      } catch (e) {
        if (cancelled) return;
        setUrlError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoadingUrl(false);
      }
    })();
    return () => {
      cancelled = true;
      if (blobObjectUrl) URL.revokeObjectURL(blobObjectUrl);
    };
  }, [doc.r2_key]);

  function fmtFecha(iso: string) {
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  function fmtKb(kb: number) {
    return kb >= 1000 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
  }

  function handleDownload() {
    if (!signedUrl) {
      alert('Aún no se cargó la URL de descarga. Espera o cierra y reintenta.');
      return;
    }
    // Redirigir al signed URL — el navegador descarga (R2 sirve con
    // Content-Disposition: attachment cuando aplica) o abre inline.
    // Usamos window.open para no perder la pestaña actual.
    const a = document.createElement('a');
    a.href = signedUrl;
    a.download = doc.nombre;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // createPortal monta el overlay en document.body para que escape de
  // cualquier stacking context del workspace (transform/perspective/filter
  // en algún ancestro hacen que `position: fixed` se vuelva relativo al
  // ancestro y el modal quede atrapado en el área del main, solapándose
  // con el sidebar). Al renderizar fuera del árbol DOM del shell, el
  // overlay sí cubre toda la viewport.
  return createPortal(
    <div className="doc-modal-overlay" onClick={onClose}>
      <div className="doc-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="doc-modal-header">
          <div className="doc-modal-title-row">
            {isPdf ? (
              <span className="doc-modal-type-badge pdf">PDF</span>
            ) : isImg ? (
              <span className="doc-modal-type-badge img">IMG</span>
            ) : (
              <span className="doc-modal-type-badge other">DOC</span>
            )}
            <span className="doc-modal-filename">{doc.nombre}</span>
            {typeof currentIndex === 'number' && typeof total === 'number' && total > 1 && (
              <span className="doc-modal-counter" aria-label={`Documento ${currentIndex + 1} de ${total}`}>
                {currentIndex + 1} / {total}
              </span>
            )}
          </div>
          <div className="doc-modal-header-actions">
            {(onPrev || onNext) && (
              <>
                <button
                  type="button"
                  className="doc-modal-nav"
                  onClick={onPrev}
                  disabled={!onPrev}
                  aria-label="Documento anterior (←)"
                  title="Documento anterior (←)"
                >
                  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className="doc-modal-nav"
                  onClick={onNext}
                  disabled={!onNext}
                  aria-label="Documento siguiente (→)"
                  title="Documento siguiente (→)"
                >
                  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </>
            )}
            <button className="doc-modal-close" onClick={onClose} aria-label="Cerrar (Esc)" title="Cerrar (Esc)">
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Preview real cuando hay signedUrl; placeholder cuando no. */}
        <div className="doc-modal-preview">
          {!doc.r2_key ? (
            // Mock o doc sin r2_key (legacy): placeholder estilo
            isPdf ? <PdfPreview nombre={doc.nombre} /> : isImg ? <ImgPreview /> : <GenericPreview />
          ) : loadingUrl ? (
            <div className="doc-preview-img" style={{ minHeight: 320 }}>
              <p className="doc-preview-note">Cargando previsualización…</p>
            </div>
          ) : urlError ? (
            <div className="doc-preview-img" style={{ minHeight: 320 }}>
              <p className="doc-preview-note" style={{ color: 'var(--st-rojo, #b00)' }}>
                No se pudo cargar el archivo: {urlError}
              </p>
            </div>
          ) : blobUrl && isImg ? (
            <div className="doc-preview-img" style={{ minHeight: 320, padding: 0 }}>
              <img
                src={blobUrl}
                alt={doc.nombre}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
                onError={() => setUrlError('Imagen no se pudo renderizar')}
              />
            </div>
          ) : blobUrl && isPdf ? (
            <iframe
              src={blobUrl}
              title={doc.nombre}
              style={{ width: '100%', height: '70vh', border: 0, borderRadius: 8, background: '#fff' }}
            />
          ) : signedUrl ? (
            // Tipo no previsualizable nativamente (docx, xlsx, etc.) →
            // mostrar placeholder + invitar a descargar.
            <div className="doc-preview-img" style={{ minHeight: 320 }}>
              <svg viewBox="0 0 24 24" width={40} height={40} fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--teal)', opacity: 0.7 }}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
              <p className="doc-preview-note">
                Vista previa no disponible para .{ext} — usa "Descargar" para abrirlo.
              </p>
            </div>
          ) : (
            <GenericPreview />
          )}
        </div>

        {/* Footer */}
        <div className="doc-modal-footer">
          <div className="doc-modal-meta">
            <span>{fmtFecha(doc.fecha)}</span>
            <span className="doc-modal-sep">·</span>
            <span>{fmtKb(doc.kb)}</span>
          </div>
          <div className="doc-modal-actions">
            <button className="doc-modal-btn-ghost" onClick={onClose}>Cerrar</button>
            <button
              className="doc-modal-btn-primary"
              onClick={handleDownload}
              disabled={!doc.r2_key || loadingUrl || !signedUrl}
              title={!doc.r2_key ? 'Mock — sin archivo real' : loadingUrl ? 'Cargando…' : 'Descargar'}
            >
              <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Descargar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PdfPreview({ nombre }: { nombre: string }) {
  return (
    <div className="doc-preview-pdf">
      <div className="doc-preview-pdf-page">
        <div className="doc-preview-pdf-header">
          <div className="doc-preview-pdf-logo" />
          <div className="doc-preview-pdf-lines">
            <div className="doc-preview-line w-60" />
            <div className="doc-preview-line w-40" />
          </div>
        </div>
        <div className="doc-preview-pdf-body">
          {[80,65,90,55,72,88,45,68,78,50,60,85].map((w, i) => (
            <div key={i} className="doc-preview-line" style={{ width: `${w}%`, animationDelay: `${i * 30}ms` }} />
          ))}
        </div>
        <div className="doc-preview-pdf-footer">
          <div className="doc-preview-line w-30" />
        </div>
      </div>
      <p className="doc-preview-note">{nombre}</p>
    </div>
  );
}

function ImgPreview() {
  return (
    <div className="doc-preview-img">
      <svg viewBox="0 0 24 24" width={40} height={40} fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--teal)', opacity: 0.7 }}>
        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
      </svg>
      <p className="doc-preview-note">Previsualización disponible al conectar el almacenamiento</p>
    </div>
  );
}

function GenericPreview() {
  return (
    <div className="doc-preview-img">
      <svg viewBox="0 0 24 24" width={40} height={40} fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--teal)', opacity: 0.7 }}>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      </svg>
      <p className="doc-preview-note">Previsualización disponible al conectar el almacenamiento</p>
    </div>
  );
}

// =============================================================================
// ResolverIrregularidadesModal — UI para que un admin cierre 1+ irregularidades
// activas de una parcela. Mig 0076 implementa el RPC con guard de rol.
//
// UX:
//   - Lista de tarjetas, una por irregularidad activa.
//   - Cada tarjeta tiene su propio textarea opcional "¿Cómo se resolvió?"
//     y botón "Resolver" individual — permite resolver de una por vez,
//     respetando el ritmo del admin.
//   - Tras resolver una, la fila desaparece (el padre invalida el cache y
//     re-fetcha; el modal observa el array prop). Si quedan 0, auto-cierra.
//   - Esc cierra. Backdrop click NO cierra (evita perder textos a medio
//     escribir por click accidental).
// =============================================================================
function ResolverIrregularidadesModal({
  parcelaCodigo,
  irregularidades,
  onClose,
  onResolved,
}: {
  parcelaCodigo: string;
  irregularidades: IrregularidadActivaRow[];
  onClose: () => void;
  /** Llamado tras cada resolución exitosa. Padre debería invalidar el
   *  cache de `irregularidades_list` para que la lista se refresque. */
  onResolved: () => void;
}) {
  // Auto-cerrar si el padre nos pasa lista vacía (todas resueltas).
  useEffect(() => {
    if (irregularidades.length === 0) {
      const t = window.setTimeout(onClose, 600);  // pequeña pausa para que el toast se vea
      return () => window.clearTimeout(t);
    }
    return;
  }, [irregularidades.length, onClose]);

  // Esc cierra.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="doc-modal-overlay" role="dialog" aria-modal="true">
      <div
        className="doc-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 'min(640px, 92vw)' }}
      >
        <div className="doc-modal-header">
          <div className="doc-modal-title-row">
            <span className="doc-modal-type-badge other" style={{ background: 'rgba(245,158,11,0.18)', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.4)' }}>RESOLVER</span>
            <span className="doc-modal-filename">{parcelaCodigo}</span>
            {irregularidades.length > 1 && (
              <span className="doc-modal-counter" aria-label={`${irregularidades.length} irregularidades activas`}>
                {irregularidades.length} activas
              </span>
            )}
          </div>
          <div className="doc-modal-header-actions">
            <button className="doc-modal-close" onClick={onClose} aria-label="Cerrar (Esc)" title="Cerrar (Esc)">
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="doc-modal-preview" style={{ padding: 18, display: 'block', minHeight: 0 }}>
          {irregularidades.length === 0 ? (
            <p style={{ color: 'var(--text-dim)', fontSize: 12, textAlign: 'center', padding: 20 }}>
              Todas las irregularidades de esta parcela ya fueron resueltas.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {irregularidades.map((irr) => (
                <ResolverIrregularidadItem key={irr.id} irr={irr} onResolved={onResolved} />
              ))}
            </ul>
          )}
        </div>
        <div className="doc-modal-footer">
          <div className="doc-modal-meta">
            <span>Acción solo disponible para admin · queda en bitácora</span>
          </div>
          <div className="doc-modal-actions">
            <button className="doc-modal-btn-ghost" onClick={onClose}>
              {irregularidades.length === 0 ? 'Cerrar' : 'Listo'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ResolverIrregularidadItem({
  irr,
  onResolved,
}: {
  irr: IrregularidadActivaRow;
  onResolved: () => void;
}) {
  const [resolucion, setResolucion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleResolve() {
    setError(null);
    setBusy(true);
    try {
      const sb = getSupabase();
      const { error: rpcErr } = await sb.rpc('resolver_irregularidad', {
        p_id:         irr.id,
        p_resolucion: resolucion.trim() || null,
      });
      if (rpcErr) throw rpcErr;
      showToast(`Irregularidad resuelta · ${irr.subtipo.slice(0, 60)}`);
      onResolved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Mapea los errores conocidos del RPC a mensajes humanos.
      if (/requiere rol admin_aosenuma/.test(msg)) {
        setError('Solo un admin aosenuma puede resolver. Pide soporte.');
      } else if (/ya está resuelta/.test(msg)) {
        setError('Ya fue resuelta por otro admin. Refresca para ver el estado actual.');
      } else if (/tenant mismatch/.test(msg)) {
        setError('Sesión inconsistente con el expediente. Cierra sesión y vuelve a entrar.');
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      style={{
        padding: '12px 14px',
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
        <strong style={{ fontSize: 12.5, color: 'var(--cream)' }}>{irr.subtipo}</strong>
        <span
          style={{
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: 999,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            background: 'rgba(245,158,11,0.15)',
            color: '#fbbf24',
            border: '1px solid rgba(245,158,11,0.35)',
          }}
        >
          {irr.gravedad}
        </span>
      </div>
      {irr.descripcion && (
        <p style={{ margin: '4px 0 8px', fontSize: 11.5, color: 'var(--text-mid)', whiteSpace: 'pre-wrap' }}>
          {irr.descripcion}
        </p>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
        <span style={{ color: 'var(--text-dim)' }}>¿Cómo se resolvió? (opcional)</span>
        <textarea
          value={resolucion}
          onChange={(e) => setResolucion(e.target.value)}
          maxLength={500}
          rows={2}
          placeholder="Ej.: Se aportó constancia de derechos vigente; RAN actualizado el 22 may."
          style={{ padding: '6px 8px', fontSize: 12, resize: 'vertical' }}
        />
        <span style={{ fontSize: 10, color: 'var(--text-dim)', textAlign: 'right' }}>
          {resolucion.length}/500
        </span>
      </label>
      {error && (
        <div role="alert" style={{ color: 'var(--st-rojo)', fontSize: 11, marginTop: 6 }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => void handleResolve()}
          disabled={busy}
          className="doc-modal-btn-primary"
        >
          {busy ? 'Resolviendo…' : 'Resolver y archivar'}
        </button>
      </div>
    </li>
  );
}
